import React from 'react'
import { convertUTCtoMSK } from '../../../../../../../utils'
import { Table } from '../../../../../../../components/Table'
import { TableHead } from '../../../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../../../components/Table/TableBody'

import { TruncatedValue } from '../../../../../../common/TruncatedValue'
import { ClientCell } from '../../../../../../common/ClientCell'
import { PayoutTokenLink } from '../../../../../../common/PayoutTokenLink'
import { MatchingTicketLink } from '../../../../../../common/MatchingTicketLink'
import { OverpaymentRecord } from '../../core/response.types'
import { PAY_VIA_LOCAL_ADAPTER_LABEL, UI_STATUS_LABEL, VIA_CASH_BOX_LABEL } from '../../core/consts'
import { uiStatusColor } from '../../core/status'

interface Props {
  items: OverpaymentRecord[]
  loading?: boolean
  /** Не задан — колонка действий не рисуется вовсе (роль без права на действие) */
  onPayViaLocalAdapter?: (overpayment: OverpaymentRecord) => void
}

const OverpaymentsTable: React.FC<Props> = ({ items, loading, onPayViaLocalAdapter }) => {
  if (loading) {
    return <div className='text-muted'>Загрузка…</div>
  }
  if (!items.length) {
    return <div className='text-muted'>Записей не найдено</div>
  }

  return (
    <Table>
      <TableHead>
        <tr>
          <th>ID</th>
          <th>Создано</th>
          <th>Статус</th>
          <th>Причина</th>
          <th>Депозит (виновник)</th>
          <th>Исх. выплата</th>
          <th>Компенс. выплата</th>
          <th>Сумма</th>
          <th>Получатель</th>
          <th>Мерчант-получатель</th>
          <th>Виновник</th>
          <th>Мерчант-виновник</th>
          <th>Комментарий</th>
          <th>Сотрудник</th>
          <th>Закрыто</th>
          {onPayViaLocalAdapter && <th>Действия</th>}
        </tr>
      </TableHead>
      <TableBody>
        {items.map((o) => (
          <tr key={o.id}>
            <td>{o.id}</td>
            <td>{convertUTCtoMSK(o.createdAt)}</td>
            <td style={{ maxWidth: 180 }}>
              <span className={`badge bg-${uiStatusColor(o.status)}`}>
                {UI_STATUS_LABEL[o.status]}
              </span>
              {/* Подпись идёт под бейджем ЛЮБОГО статуса: касса остаётся фактом и у отменённой
                  записи, и у той, что ещё в работе. */}
              {o.cashBox && (
                <div className='small fw-bold'>
                  {VIA_CASH_BOX_LABEL} <TruncatedValue value={o.cashBox.title} />
                </div>
              )}
            </td>
            <td>{o.reason.title}</td>
            <td>
              <TokenCell token={o.sourcePaymentTicket.token} />
            </td>
            <td>
              <PayoutTokenLink token={o.originalPayoutTicket.token} />
            </td>
            <td>
              <PayoutTokenLink token={o.compensationPayoutTicket.token} />
            </td>
            <td>
              {o.compensationPayoutTicket.amount} {o.compensationPayoutTicket.currency ?? ''}
            </td>
            <ClientCell externalId={o.recipient.externalId} token={o.recipient.clientToken} />
            <td>{o.recipient.merchantTitle ?? '-'}</td>
            <ClientCell externalId={o.culprit.externalId} token={o.culprit.clientToken} />
            <td>{o.culprit.merchantTitle ?? '-'}</td>
            <td className='text-muted small' style={{ maxWidth: 200 }}>
              {o.managerComment ?? '-'}
            </td>
            <td>{o.createdBy.uuid}</td>
            <td>{o.closedAt ? convertUTCtoMSK(o.closedAt) : '-'}</td>
            {onPayViaLocalAdapter && (
              <td>
                {o.canPayViaLocalAdapter ? (
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => onPayViaLocalAdapter(o)}
                  >
                    {PAY_VIA_LOCAL_ADAPTER_LABEL}
                  </button>
                ) : (
                  '-'
                )}
              </td>
            )}
          </tr>
        ))}
      </TableBody>
    </Table>
  )
}

const TokenCell: React.FC<{ token: string }> = ({ token }) => (
  <span className='text-monospace small'>
    <MatchingTicketLink token={token} />
  </span>
)

export default OverpaymentsTable
